import React, { useRef, useState, useCallback, useMemo } from "react";

import { Box, keyframes } from "@mui/material";
import Draggable, { DraggableData, DraggableEvent } from "react-draggable";

import {
  TextOverlay,
  TextAnimation,
  REFERENCE_PREVIEW_HEIGHT,
} from "../../types";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideLeft = keyframes`
  from { opacity: 0; transform: translateX(30px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideRight = keyframes`
  from { opacity: 0; transform: translateX(-30px); }
  to { opacity: 1; transform: translateX(0); }
`;

const zoomIn = keyframes`
  from { opacity: 0; transform: scale(0.3); }
  to { opacity: 1; transform: scale(1); }
`;

const zoomOut = keyframes`
  from { opacity: 0; transform: scale(1.5); }
  to { opacity: 1; transform: scale(1); }
`;

const pop = keyframes`
  0% { opacity: 0; transform: scale(0); }
  70% { transform: scale(1.15); }
  100% { opacity: 1; transform: scale(1); }
`;

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-12px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const ANIMATION_MAP: Record<
  Exclude<TextAnimation, "none" | "typewriter">,
  {
    keyframe: ReturnType<typeof keyframes>;
    duration: string;
    iterationCount: string;
    fillMode: string;
  }
> = {
  "fade-in": {
    keyframe: fadeIn,
    duration: "0.6s",
    iterationCount: "1",
    fillMode: "forwards",
  },
  "fade-out": {
    keyframe: fadeOut,
    duration: "0.6s",
    iterationCount: "1",
    fillMode: "forwards",
  },
  "slide-up": {
    keyframe: slideUp,
    duration: "0.6s",
    iterationCount: "1",
    fillMode: "forwards",
  },
  "slide-down": {
    keyframe: slideDown,
    duration: "0.6s",
    iterationCount: "1",
    fillMode: "forwards",
  },
  "slide-left": {
    keyframe: slideLeft,
    duration: "0.6s",
    iterationCount: "1",
    fillMode: "forwards",
  },
  "slide-right": {
    keyframe: slideRight,
    duration: "0.6s",
    iterationCount: "1",
    fillMode: "forwards",
  },
  "zoom-in": {
    keyframe: zoomIn,
    duration: "0.5s",
    iterationCount: "1",
    fillMode: "forwards",
  },
  "zoom-out": {
    keyframe: zoomOut,
    duration: "0.5s",
    iterationCount: "1",
    fillMode: "forwards",
  },
  pop: {
    keyframe: pop,
    duration: "0.4s",
    iterationCount: "1",
    fillMode: "forwards",
  },
  bounce: {
    keyframe: bounce,
    duration: "1s",
    iterationCount: "infinite",
    fillMode: "none",
  },
  pulse: {
    keyframe: pulse,
    duration: "1.5s",
    iterationCount: "infinite",
    fillMode: "none",
  },
  rotate: {
    keyframe: rotate,
    duration: "2s",
    iterationCount: "infinite",
    fillMode: "none",
  },
};

interface TextOverlayRendererProps {
  overlay: TextOverlay;
  containerWidth: number;
  containerHeight: number;
  currentTimeMs: number;
  onUpdate: (id: string, updates: Partial<TextOverlay>) => void;
}

export default function TextOverlayRenderer({
  overlay,
  containerWidth,
  containerHeight,
  currentTimeMs,
  onUpdate,
}: TextOverlayRendererProps) {
  const nodeRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);

  const isVisible =
    currentTimeMs >= overlay.startMs && currentTimeMs <= overlay.endMs;

  const fontScale = containerHeight / REFERENCE_PREVIEW_HEIGHT;
  const scaledFontSize = Math.round(overlay.fontSize * fontScale);

  const pxX = (overlay.x / 100) * containerWidth;
  const pxY = (overlay.y / 100) * containerHeight;
  const pxWidth = (overlay.width / 100) * containerWidth;

  const handleDragStop = useCallback(
    (_e: DraggableEvent, data: DraggableData) => {
      const newX = (data.x / containerWidth) * 100;
      const newY = (data.y / containerHeight) * 100;
      onUpdate(overlay.id, { x: newX, y: newY });
    },
    [containerWidth, containerHeight, overlay.id, onUpdate],
  );

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setIsResizing(true);

      const startX = e.clientX;
      const startW = pxWidth;

      const onMouseMove = (ev: MouseEvent) => {
        const newW = Math.max(30, startW + (ev.clientX - startX));
        onUpdate(overlay.id, {
          width: (newW / containerWidth) * 100,
        });
      };

      const onMouseUp = () => {
        setIsResizing(false);
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
      };

      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    },
    [pxWidth, containerWidth, overlay.id, onUpdate],
  );

  const animation = overlay.animation || "none";

  const animationSx = useMemo(() => {
    if (animation === "none" || animation === "typewriter") return {};
    const config = ANIMATION_MAP[animation];
    return {
      animation: `${config.keyframe} ${config.duration} ease ${config.iterationCount}`,
      animationFillMode: config.fillMode,
    };
  }, [animation]);

  // Typewriter: reveal characters based on elapsed time
  const displayText = useMemo(() => {
    if (animation !== "typewriter") return overlay.text;
    const elapsed = currentTimeMs - overlay.startMs;
    const totalDuration = Math.min(
      2000,
      (overlay.endMs - overlay.startMs) * 0.3,
    );
    const progress = Math.min(1, Math.max(0, elapsed / totalDuration));
    const charCount = Math.floor(progress * overlay.text.length);
    return overlay.text.slice(0, charCount);
  }, [animation, overlay.text, overlay.startMs, overlay.endMs, currentTimeMs]);

  if (!isVisible) return null;

  return (
    <Draggable
      nodeRef={nodeRef}
      position={{ x: pxX, y: pxY }}
      onStop={handleDragStop}
      disabled={isResizing}
      bounds="parent"
    >
      <Box
        ref={nodeRef}
        sx={{
          position: "absolute",
          width: pxWidth,
          cursor: "move",
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: overlay.backgroundColor,
          borderRadius: "4px",
          overflow: "hidden",
          "&:hover .resize-handle": {
            opacity: 1,
          },
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: `${Math.round(4 * fontScale)}px ${Math.round(8 * fontScale)}px`,
            boxSizing: "border-box",
            pointerEvents: "none",
            userSelect: "none",
            ...animationSx,
          }}
        >
          <span
            style={{
              fontFamily: overlay.fontFamily,
              fontSize: scaledFontSize,
              color: overlay.color,
              fontWeight: overlay.bold ? 700 : 400,
              fontStyle: overlay.italic ? "italic" : "normal",
              textAlign: "center",
              wordBreak: "break-word",
              lineHeight: 1.2,
            }}
          >
            {displayText}
          </span>
        </Box>
        <Box
          className="resize-handle"
          onMouseDown={handleResizeMouseDown}
          sx={{
            position: "absolute",
            bottom: 0,
            right: 0,
            width: 14,
            height: 14,
            cursor: "ew-resize",
            background: "rgba(255,255,255,0.8)",
            borderRadius: "2px 0 0 0",
            opacity: 0,
            transition: "opacity 0.2s",
            "&:hover": {
              background: "rgba(100,150,255,0.9)",
            },
          }}
        />
      </Box>
    </Draggable>
  );
}
